// CHG-132 Phase D: 보관 프로젝트 읽기 권한 요청
import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

export type AccessRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELED';

export interface AccessRequest {
  id: string;
  projectId: string;
  // CHG-132 Phase E REQ-PA-022: 요청자 탈퇴 시 null (감사 보존)
  requesterId: string | null;
  reason: string;
  status: AccessRequestStatus;
  approverId: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
}

export interface AccessRequestWithUsers extends AccessRequest {
  requester: { id: string; name: string; email: string; avatarUrl: string | null } | null;
  approver: { id: string; name: string } | null;
  project?: { id: string; name: string; projectKey: string; archivedAt: string | null };
}

export interface AccessRequestFilters {
  status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELED' | 'ALL';
  q?: string;
  from?: string;
  to?: string;
  projectId?: string;
}

export async function createAccessRequest(projectId: string, reason: string): Promise<{ request: AccessRequest; notifiedApproversCount: number }> {
  const res = await api.post<ApiResponse<{ request: AccessRequest; notifiedApproversCount: number }>>(
    `/projects/${projectId}/space-access-requests`,
    { reason },
  );
  return res.data.data!;
}

export async function decideAccessRequest(
  projectId: string,
  requestId: string,
  payload: { decision: 'APPROVE' | 'REJECT'; note?: string },
): Promise<AccessRequest> {
  const res = await api.put<ApiResponse<AccessRequest>>(
    `/projects/${projectId}/space-access-requests/${requestId}`,
    payload,
  );
  return res.data.data!;
}

export async function cancelAccessRequest(projectId: string, requestId: string): Promise<AccessRequest> {
  const res = await api.delete<ApiResponse<AccessRequest>>(
    `/projects/${projectId}/space-access-requests/${requestId}`,
  );
  return res.data.data!;
}

export async function fetchAccessRequest(projectId: string, requestId: string): Promise<AccessRequestWithUsers> {
  const res = await api.get<ApiResponse<AccessRequestWithUsers>>(
    `/projects/${projectId}/space-access-requests/${requestId}`,
  );
  return res.data.data!;
}

function buildQuery(filters: AccessRequestFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.q) params.set('q', filters.q);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.projectId) params.set('projectId', filters.projectId);
  return params.toString();
}

export async function listAccessRequests(
  projectId: string,
  filters: AccessRequestFilters = {},
): Promise<AccessRequestWithUsers[]> {
  const qs = buildQuery(filters);
  const res = await api.get<ApiResponse<AccessRequestWithUsers[]>>(
    `/projects/${projectId}/space-access-requests${qs ? '?' + qs : ''}`,
  );
  return res.data.data!;
}

// CHG-132 Phase E REQ-PA-024: 조직 통합 조회
export async function listOrgAccessRequests(
  orgId: string,
  filters: AccessRequestFilters = {},
): Promise<AccessRequestWithUsers[]> {
  const qs = buildQuery(filters);
  const res = await api.get<ApiResponse<AccessRequestWithUsers[]>>(
    `/organizations/${orgId}/space-access-requests${qs ? '?' + qs : ''}`,
  );
  return res.data.data!;
}
